/**
 * Static password helper functions
 */
export abstract class PasswordUtil {

  /**
   * Keys containing password to hide in text/logs
   */
  static readonly KEYS_TO_HIDE: readonly string[] = [
    'password', 'Password', 'apiPassword', 'snmpPrivPassword', 'snmpAuthPassword', 'sshPassword', 'cliPassword', 'ftpPassword', 'proxyPassword', 'dsmadmcPassword', 'SMTP_PASS',
    'token', 'Token', 'apiToken',
    'secret', 'aws_secret_access_key', 'apiKey', 'CLIENT_SECRET',
    'authorization'
  ];

  /**
   * Returns antd RuleConfigs for password rules settings.
   *
   * @param passwordRules the password rules settings
   *
   * @return antd RuleConfigs for password rules settings
   */
  static getRules(passwordRules) {
    const validators = {
      PASSWORD_MIN_LENGTH: (val) => ({
        min: val,
        message: `Password must be minimum ${val} characters.`,
      }),
      PASSWORD_NUMERALS: (_) => ({
        pattern: /[0-9]/,
        message: `Password must contain a digit.`,
      }),
      PASSWORD_UPPERCASE: (_) => ({
        pattern: /[A-Z]/,
        message: `Password must contain an uppercase letter.`,
      }),
      PASSWORD_LOWERCASE: (_) => ({
        pattern: /[a-z]/,
        message: `Password must contain a lowercase letter.`,
      }),
      PASSWORD_SPECIAL: (_) => ({
        pattern: /[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/, // taken from https://owasp.org/www-community/password-special-characters
        message: `Password must contain a special character.`,
      }),
    };
    const rules: any = [
      { max: 50, message: "Password can not be longer than 50 characters." },
    ];
    if (!passwordRules.PASSWORD_ENABLED) {
      rules.push({ min: 5, message: "Password must be minimum 5 characters." });
    } else {
      for (const key in passwordRules) {
        if (passwordRules[key] && (validators[key] !== undefined)) {
          rules.push(validators[key](passwordRules[key]));
        }
      }
    }
    return rules;
  }

  /**
   * Returns true if password passes all the rules.
   *
   * @param password the password
   * @param passwordRules the password rules settings
   *
   * @return true if password passes all the rules.
   */
  static isStrong(password, passwordRules) {
    return (this.validate(password, passwordRules).length === 0);
  }

  /**
   * Returns an array of messages for rules that the password violates.
   * An empty array means the password passes all rules.
   *
   * @param password the password
   * @param passwordRules the password rules settings
   *
   * @return array of messages for rules that the password violates
   */
  static validate(password, passwordRules) {
    const validators = {
      min: (str, min) => (str.length >= min),
      max: (str, max) => (str.length <= max),
      pattern: (str, pattern) => pattern.test(str),
    };
    const rules = this.getRules(passwordRules);
    const ret = [];
    for (const rule of rules) {
      for (const v in validators) {
        if ((rule[v] !== undefined)
          && !validators[v](password, rule[v])) {
          ret.push(rule.message);
        }
      }
    }
    return ret;
  }

  /**
   * Returns default password rules settings.
   */
  static getDefaults() {
    return {
      PASSWORD_ENABLED: false,
      PASSWORD_MIN_LENGTH: 8,
      PASSWORD_NUMERALS: true,
      PASSWORD_UPPERCASE: true,
      PASSWORD_LOWERCASE: true,
      PASSWORD_SPECIAL: true,
      PASSWORD_HISTORY_COUNT: 1,
    };
  }

}